import { useEffect, useState } from "react";
import { BIFROST_FLOW } from "../../content/bifrost-site";

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(query.matches);
    const listener = (e: MediaQueryListEvent) => setReduced(e.matches);
    query.addEventListener?.("change", listener);
    return () => query.removeEventListener?.("change", listener);
  }, []);
  return reduced;
}

/** Org A ↔ Bifrost trust exchange ↔ Org B, stepping through the six stages. */
export function FederatedFlow({ compact = false }: { compact?: boolean }) {
  const reduced = useReducedMotion();
  const steps = BIFROST_FLOW.steps;
  const [active, setActive] = useState(0);
  const [refuse, setRefuse] = useState(false);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    if (reduced || !playing) return;
    const id = window.setInterval(
      () => setActive((i) => (i + 1) % steps.length),
      2200,
    );
    return () => window.clearInterval(id);
  }, [reduced, playing, steps.length]);

  const refusedAt = 2;
  const stopped = refuse && active > refusedAt;
  const fromB = active < 3;

  return (
    <figure className={`bf-flow${compact ? " bf-flow--compact" : ""}`} aria-label="Federated exchange between two organizations">
      <div className="bf-flow__stage">
        <div className="bf-org">
          <span className="bf-org__tag">Organization A</span>
          <b>Evidence owner · decides</b>
          <ul>
            <li>Own identity and keys</li>
            <li>Own evidence boundary</li>
            <li>Own Cedar policy</li>
          </ul>
        </div>
        <div className="bf-flow__bridge">
          <div className={`bf-flow__packet${fromB ? " is-inbound" : " is-outbound"}${stopped || (refuse && active === refusedAt) ? " is-refused" : ""}`} aria-hidden="true" />
          <div className="bf-flow__core">
            <img src="/brand/bifrost-mark.png" alt="" width={44} height={44} />
            <b>Bifrost trust exchange</b>
            <span className={`bf-flow__verdict${refuse && active >= refusedAt ? " is-deny" : ""}`}>
              {refuse && active >= refusedAt
                ? "Refused · reason recorded"
                : steps[active]?.title}
            </span>
          </div>
        </div>
        <div className="bf-org">
          <span className="bf-org__tag">Organization B</span>
          <b>Requesting agent · asks</b>
          <ul>
            <li>Own identity and keys</li>
            <li>Delegates bounded authority</li>
            <li>Verifies independently</li>
          </ul>
        </div>
      </div>

      <ol className="bf-flow__steps">
        {steps.map((step, i) => {
          const skipped = refuse && i > refusedAt && i < 4;
          return (
            <li key={step.key} className={`${i === active ? "is-active" : ""}${skipped ? " is-skipped" : ""}`}>
              <button type="button" onClick={() => { setActive(i); setPlaying(false); }} aria-pressed={i === active}>
                <span className="bf-flow__num">{i + 1}</span>
                <b>{step.title}</b>
              </button>
              {!compact && <p>{refuse && i === refusedAt ? "Policy does not permit this request. Bifrost issues a signed deny and releases nothing." : step.body}</p>}
            </li>
          );
        })}
      </ol>

      <div className="bf-flow__controls">
        <button type="button" className="btn btn--quiet" onClick={() => setPlaying((p) => !p)} disabled={reduced}>
          {playing && !reduced ? "Pause" : "Play"}
        </button>
        <label className="bf-switch">
          <input type="checkbox" checked={refuse} onChange={(e) => setRefuse(e.target.checked)} />
          <span>Show a refusal</span>
        </label>
      </div>
      {!compact && <figcaption>{BIFROST_FLOW.refusal}</figcaption>}
    </figure>
  );
}

type Report = {
  integrity: "valid" | "invalid";
  authority: "valid" | "invalid" | "unknown";
  authorization: "permit" | "deny" | "unknown";
  execution: "reported" | "none" | "invalid";
  failures: string[];
};

type Scenario = {
  key: string;
  label: string;
  summary: string;
  stopAfter: number;
  report: Report;
};

const ACTION_STEPS = [
  { who: "B", title: "Organization B delegates limited authority", body: "Delegation: action approve-vendor-evidence-sharing, purpose vendor-onboarding, limit max_documents 25, expires in 24 hours, bound to the agent’s DPoP key." },
  { who: "B", title: "The agent signs an action proposal", body: "Envelope: {action_type, parameters, parameters_sha256, purpose, nonce, expires_at}, signed with the agent’s key (bifrost-action-proposal+jws)." },
  { who: "A", title: "Organization A verifies the delegation", body: "A fetches B’s published keys, checks the signed delegation credential, its narrowing, its live revocation status, and that the proposal key matches the delegation." },
  { who: "A", title: "Organization A evaluates its counterparty policy", body: "Cedar DecideAction runs on A’s grant for B against the exact envelope: action, purpose, and parameters within limits." },
  { who: "A", title: "Bifrost issues a signed permit or deny", body: "The decision (bifrost-action-decision+jws) is bound to parameters_sha256 and the proposal hash. Any changed parameter needs a new decision." },
  { who: "B", title: "Organization B accepts the decision", body: "B verifies A’s signature and signs an acceptance (bifrost-action-acceptance+jws) linked to the decision." },
  { who: "A", title: "Execution is recorded only when reported", body: "A’s own business system performs the action. Bifrost records an execution report only when one is submitted; until then execution reads none." },
  { who: "AB", title: "Either side verifies the action chain", body: "The exported chain (bifrost-action-chain/1) is checked offline with bifrost-verify action: signatures, hash links, log inclusion, delegation and decision." },
];

const SCENARIOS: Scenario[] = [
  {
    key: "permit",
    label: "Permitted and executed",
    summary: "Every claim checks out, and the execution was independently reported.",
    stopAfter: 7,
    report: { integrity: "valid", authority: "valid", authorization: "permit", execution: "reported", failures: [] },
  },
  {
    key: "pending",
    label: "Permitted, not yet executed",
    summary: "A valid permit is not a completed action. Execution stays none until A reports it.",
    stopAfter: 7,
    report: { integrity: "valid", authority: "valid", authorization: "permit", execution: "none", failures: [] },
  },
  {
    key: "deny",
    label: "Denied by A’s policy",
    summary: "B’s authority is real, but A’s grant does not cover this purpose. The deny is signed and verifiable too.",
    stopAfter: 7,
    report: { integrity: "valid", authority: "valid", authorization: "deny", execution: "none", failures: [] },
  },
  {
    key: "revoked",
    label: "Delegation revoked before the decision",
    summary: "B revoked the agent’s delegation. Authority fails, even though every signature is intact.",
    stopAfter: 7,
    report: { integrity: "valid", authority: "invalid", authorization: "deny", execution: "none", failures: ["delegation_revoked_at_decision"] },
  },
  {
    key: "tampered",
    label: "Parameters changed after signing",
    summary: "Someone edited the proposal after it was signed. Integrity fails, so nothing else can be concluded.",
    stopAfter: 7,
    report: { integrity: "invalid", authority: "valid", authorization: "unknown", execution: "none", failures: ["proposal:sha256_mismatch"] },
  },
];

const GOOD = new Set(["valid", "permit", "reported"]);
const NEUTRAL = new Set(["unknown", "none"]);

function ResultTile({ name, value, hint }: { name: string; value: string; hint: string }) {
  const tone = GOOD.has(value) ? "ok" : NEUTRAL.has(value) ? "neutral" : "bad";
  return (
    <div className={`bf-result bf-result--${tone}`}>
      <span>{name}</span>
      <b>{value}</b>
      <small>{hint}</small>
    </div>
  );
}

/** Two-organization walk through the ADR-0034 federated action-approval model. */
export function ActionDemo() {
  const [scenarioKey, setScenarioKey] = useState("permit");
  const [step, setStep] = useState(0);
  const scenario: Scenario = SCENARIOS.find((s) => s.key === scenarioKey) ?? SCENARIOS[0]!;
  const done = step >= ACTION_STEPS.length - 1;
  const r = scenario.report;

  return (
    <div className="bf-demo">
      <div className="bf-demo__scenarios" role="radiogroup" aria-label="Scenario">
        {SCENARIOS.map((s) => (
          <button key={s.key} type="button" role="radio" aria-checked={s.key === scenarioKey} className={s.key === scenarioKey ? "is-on" : ""} onClick={() => { setScenarioKey(s.key); setStep(0); }}>
            {s.label}
          </button>
        ))}
      </div>

      <ol className="bf-demo__steps">
        {ACTION_STEPS.map((s, i) => {
          const hidden = (i === 5 || i === 6) && scenario.report.authorization !== "permit";
          const noExec = i === 6 && scenario.report.execution === "none";
          return (
            <li key={s.title} className={`${i <= step ? "is-done" : ""}${i === step ? " is-current" : ""}${hidden || noExec ? " is-muted" : ""}`}>
              <span className={`bf-demo__who bf-demo__who--${s.who}`}>{s.who === "AB" ? "A + B" : `Org ${s.who}`}</span>
              <div>
                <b>{s.title}</b>
                {i <= step && <p>{hidden ? "Skipped: there is no permit to accept or execute." : noExec ? "No execution report has been submitted." : i === 4 ? `${s.body} Here: ${r.authorization === "unknown" ? "the decision cannot be trusted" : r.authorization}.` : s.body}</p>}
              </div>
            </li>
          );
        })}
      </ol>

      <div className="bf-demo__controls">
        <button type="button" className="btn btn--primary" onClick={() => setStep((n) => Math.min(n + 1, ACTION_STEPS.length - 1))} disabled={done}>
          {done ? "Chain verified" : "Next step"}
        </button>
        <button type="button" className="btn btn--quiet" onClick={() => setStep(ACTION_STEPS.length - 1)} disabled={done}>
          Skip to verification
        </button>
        <button type="button" className="btn btn--quiet" onClick={() => setStep(0)}>
          Restart
        </button>
      </div>

      <div className={`bf-demo__report${done ? " is-ready" : ""}`} aria-live="polite">
        <p className="bf-demo__report-title">bifrost-verify action chain.json</p>
        {done ? (
          <>
            <div className="bf-results">
              <ResultTile name="Integrity" value={r.integrity} hint="Signatures, hashes, links and log inclusion" />
              <ResultTile name="Authority" value={r.authority} hint="Delegation valid, narrowed and unrevoked at decision" />
              <ResultTile name="Authorization" value={r.authorization} hint="A’s signed policy decision on this exact envelope" />
              <ResultTile name="Execution" value={r.execution} hint="Reported by A’s system, never assumed" />
            </div>
            <p className="bf-demo__summary">{scenario.summary}</p>
            <code className="bf-demo__failures">failures: {JSON.stringify(r.failures)}</code>
          </>
        ) : (
          <p className="bf-demo__summary">Step through the exchange to produce the verification report.</p>
        )}
      </div>
      <p className="bf-demo__note">Synthetic organizations and records. Result fields and failure codes are the ones the open-source verifier reports.</p>
    </div>
  );
}

const RECEIPT_RESULTS = {
  valid: {
    label: "Sample receipt",
    exit: 0,
    report: {
      authenticity: "valid",
      evidence_integrity: "valid",
      derivation: "model-entailed",
      logged: true,
      key_status: "active",
      key_custody: "bifrost",
      purpose: { code: "general-review", label: "General review" },
      failures: [] as string[],
    },
  },
  tampered: {
    label: "Tampered receipt",
    exit: 1,
    report: {
      authenticity: "invalid",
      evidence_integrity: "not_applicable",
      derivation: null,
      logged: false,
      key_status: "active",
      key_custody: "bifrost",
      purpose: null,
      failures: ["authenticity"],
    },
  },
} as const;

/** Real output of bifrost-verify 0.1.1 on the two synthetic bundles it ships. */
export function ReceiptVerifyDemo() {
  const [which, setWhich] = useState<keyof typeof RECEIPT_RESULTS>("valid");
  const result = RECEIPT_RESULTS[which];
  return (
    <div className="bf-verify">
      <div className="bf-verify__receipt">
        <p className="bf-demo__report-title">Synthetic receipt · Acme Synthetic Corp</p>
        <dl>
          <dt>Question</dt>
          <dd>How is evidence encrypted?</dd>
          <dt>Answer</dt>
          <dd>{which === "valid" ? "Acme Synthetic Corp encrypts all evidence at rest with AES-256 and requires MFA for every administrator account." : <>Signed payload replaced after signing with <code>{'{"x":1}'}</code>; header and signature unchanged.</>}</dd>
          <dt>Cited evidence</dt>
          <dd>Sample security policy · section 1 · version 1</dd>
          <dt>Signature</dt>
          <dd>Ed25519 JWS · bifrost-receipt/1</dd>
        </dl>
        <div className="bf-demo__scenarios" role="radiogroup" aria-label="Receipt">
          {(Object.keys(RECEIPT_RESULTS) as (keyof typeof RECEIPT_RESULTS)[]).map((k) => (
            <button key={k} type="button" role="radio" aria-checked={k === which} className={k === which ? "is-on" : ""} onClick={() => setWhich(k)}>
              {RECEIPT_RESULTS[k].label}
            </button>
          ))}
        </div>
      </div>
      <div className="bf-verify__out">
        <p className="bf-demo__report-title">$ bifrost-verify examples/{which === "valid" ? "bundle" : "tampered-bundle"}.json</p>
        <div className="bf-results bf-results--three">
          <ResultTile name="Authenticity" value={result.report.authenticity} hint="Signature against the issuer’s key" />
          <ResultTile name="Evidence integrity" value={result.report.evidence_integrity === "not_applicable" ? "unknown" : result.report.evidence_integrity} hint="Citation Merkle root recomputed" />
          <ResultTile name="Logged" value={result.report.logged ? "valid" : "invalid"} hint="Inclusion in the transparency log" />
        </div>
        <pre>{JSON.stringify(result.report, null, 2)}</pre>
        <p className="bf-demo__summary">exit {result.exit}{which === "tampered" ? " · the signature no longer matches the payload, so nothing in it can be relied on" : ""}</p>
      </div>
    </div>
  );
}
